import { useEffect, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { qqTrack } from "@/lib/qq-diagnostics";

const TITLE = "See How I'd Look With a Hair Transplant - Restore Hair";
const DESCRIPTION =
  "Curious how you'd look with a full head of hair? Upload a photo and see an AI preview of your potential hair transplant results with RESTORE.";
const URL =
  "https://restorehair.com/see-how-id-look-with-a-hair-transplant/";

export const Route = createFileRoute("/see-how-id-look-with-a-hair-transplant")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { property: "og:url", content: URL },
      { name: "twitter:card", content: "summary" },
    ],
    links: [{ rel: "canonical", href: URL }],
  }),
  component: SeeHowIdLookPage,
});

declare global {
  interface Window {
    hairgenAIWidget?: (
      el: HTMLElement,
      token: string,
      options?: Record<string, unknown>,
    ) => void;
  }
}

const HAIRGEN_TOKEN = "17ekp4D5bFuj5iLj";

const HAIRGEN_OPTIONS = {
  i18n: {
    "landing-subtitle": [
      "Free AI simulation on ",
      { text: "your photos", tag: "strong" },
      " in 60 seconds.",
    ],
    facts: [
      "Hair transplantation was first performed in Japan in the 1930s.",
      "It takes about 3-4 months for new hair to start growing after a transplant.",
      "Developing a full result can take up to 12-18 months.",
      "The donor hair from the back of your head is genetically resistant to balding.",
      "Modern FUE techniques leave no visible linear scar.",
      "A natural hairline is irregular on purpose.",
      "Hair grows in specific angles and swirls.",
      "Each hair graft can contain 1-4 hairs.",
    ],
    "agree-tos": [
      "By checking this box, I consent to receive automated marketing, advertising, transactional, or informational phone calls, text messages, potentially involving use of an autodialer or an artificial or prerecorded voice, from or on behalf of Restore Holdings LLC (\u201cRestore\u201d) at the phone number I provided. Consent is not a condition of purchase; message and data rates may apply, and I can opt out at any time by replying STOP. I also agree to ",
      {
        text: "Hairgen.ai's terms and conditions",
        tag: "a",
        target: "_blank",
        href: "https://www.hairgen.ai/tos/",
      },
      ", Restore Hair's ",
      {
        text: "Terms of Use",
        tag: "a",
        href: "https://restorehair.com/terms-of-use/",
      },
      " and ",
      {
        text: "Privacy Policy",
        tag: "a",
        href: "https://restorehair.com/privacy-policy/",
      },
      ", and I authorize Restore Hair to collect and process my photo and related sensitive personal information for the purpose of evaluating hair restoration options.",
    ],
  },
};

/**
 * How long the simulator gets before the page offers another way forward.
 * It normally answers in about 3 seconds. On 6 Oct 2026 HairGen's server did
 * not answer for about 15 minutes and visitors saw an empty box.
 */
const HAIRGEN_WAIT_MS = 8000;

/**
 * Why the simulator is not usable, or null when it is.
 *
 * The widget is ready when its element has rendered and its settings call
 * to api.hairgen.ai has come back. That call is cross-origin, so the browser
 * shows that it finished and, where supported, its status, but nothing else.
 */
function hairgenProblem(): "script" | "no_widget" | "api_no_response" | "api_error" | null {
  if (!window.hairgenAIWidget) return "script";
  const el = document.querySelector("hairgen-ai-widget") as (HTMLElement & { shadowRoot: ShadowRoot | null }) | null;
  if (!el?.shadowRoot) return "no_widget";
  const calls = performance
    .getEntriesByType("resource")
    .filter((e) => e.name.startsWith("https://api.hairgen.ai/")) as (PerformanceResourceTiming & { responseStatus?: number })[];
  if (!calls.length) return "api_no_response";
  if (calls.every((c) => (c.responseStatus ?? 0) >= 400)) return "api_error";
  return null;
}

function SeeHowIdLookPage() {
  const [unavailable, setUnavailable] = useState(false);

  // If the simulator has not come up in time, show another way forward and
  // tell GA4 why. Keep checking, so the notice goes away if it recovers.
  // The event name starts with qq1_ because that is the only pattern Zaraz
  // forwards to GA4 without a new trigger.
  useEffect(() => {
    let reported = false;
    const check = () => {
      const problem = hairgenProblem();
      setUnavailable(problem !== null);
      if (problem && !reported) {
        reported = true;
        qqTrack("qq1_hairgen_unavailable", { reason: problem, ms: HAIRGEN_WAIT_MS });
      }
    };
    const first = window.setTimeout(check, HAIRGEN_WAIT_MS);
    const again = window.setInterval(() => {
      if (performance.now() > HAIRGEN_WAIT_MS) check();
    }, 4000);
    const stop = window.setTimeout(() => window.clearInterval(again), 60000);
    return () => {
      window.clearTimeout(first);
      window.clearInterval(again);
      window.clearTimeout(stop);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const init = () => {
      if (cancelled) return;
      const root = document.getElementById("hairgen-ai-widget-custom");
      if (!root || !window.hairgenAIWidget) return;
      if (root.dataset["hairgenInit"] === "true") return;
      root.dataset["hairgenInit"] = "true";
      window.hairgenAIWidget(root, HAIRGEN_TOKEN, HAIRGEN_OPTIONS);
    };

    const existing = document.querySelector<HTMLScriptElement>(
      'script[src="https://www.hairgen.ai/js/embed-1.js"]',
    );
    if (existing) {
      if (window.hairgenAIWidget) init();
      else existing.addEventListener("load", init, { once: true });
    } else {
      const script = document.createElement("script");
      script.src = "https://www.hairgen.ai/js/embed-1.js";
      script.async = true;
      script.addEventListener("load", init, { once: true });
      document.body.appendChild(script);
    }

    return () => {
      cancelled = true;
    };
  }, []);

  // Hide the "Powered by Hairgen.ai" badge inside the widget's shadow DOM
  useEffect(() => {
    let cancelled = false;
    const timer = window.setInterval(() => {
      if (cancelled) return;
      const el = (document.querySelector("hairgen-ai-widget") ??
        document
          .getElementById("hairgen-ai-widget-custom")
          ?.querySelector("*")) as
        | (HTMLElement & { shadowRoot: ShadowRoot | null })
        | null;
      const root = el?.shadowRoot;
      if (!root) return;
      if (!root.querySelector("style[data-hide-powered-by]")) {
        const style = document.createElement("style");
        style.setAttribute("data-hide-powered-by", "true");
        style.textContent = [
          ".powered-by{display:none !important;}",
          "@media (min-width: 768px){",
          // Only widen containers that actually wrap a form (the phone
          // verification / details popup). Widening every .max-w-* box also
          // stretched the before/after viewer, which crops (zooms) the render.
          "  .max-w-md:has(#phone-input-form){max-width:40rem !important;}",
          "  #phone-input-form{max-width:100% !important;}",
          "}",
        ].join("\n");
        root.appendChild(style);
      }
      window.clearInterval(timer);
    }, 300);
    window.setTimeout(() => window.clearInterval(timer), 30000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="w-full">
        <h1 className="sr-only">See How I'd Look With a Hair Transplant</h1>
        <div className="mx-auto w-full max-w-6xl px-4 py-8 md:py-12">
          {unavailable && (
            <div role="status" className="mb-6 border-2 border-primary bg-primary/5 p-5 text-center">
              <p className="text-lg font-extrabold text-foreground">
                The photo simulator is taking longer than usual.
              </p>
              <p className="mt-2 text-sm text-muted-foreground">
                You don&rsquo;t need it to get started. Send us your details and a photo, and a
                RESTORE advisor will show you what to expect.
              </p>
              <div className="mt-4 flex flex-col items-center justify-center gap-3 sm:flex-row">
                <Link
                  to="/quick-quote-part-1/"
                  className="w-full bg-primary px-6 py-3 text-sm font-bold uppercase tracking-wide text-primary-foreground sm:w-auto"
                >
                  Get my quick quote
                </Link>
                <Link to="/schedule-a-consult/" className="text-sm font-semibold text-primary underline">
                  Or book a free consultation
                </Link>
              </div>
            </div>
          )}
          <div id="hairgen-ai-widget-custom" />
        </div>
      </main>
      <Footer />
    </div>
  );
}
